import React, { createContext, useContext, useEffect, useState } from 'react';
import { Amplify } from 'aws-amplify';
import { signIn, signUp, confirmSignUp, signOut, resetPassword, confirmResetPassword, getCurrentUser, fetchUserAttributes, fetchAuthSession } from 'aws-amplify/auth';
import { Snackbar, Alert } from '@mui/material';
import { logger } from '../utils/logger';
import { sessionManager } from '../utils/sessionManager';
import { tokenManager } from '../utils/tokenManager';
import { clearAllStorage } from '../utils/storageCleanup';

// Cognito Configuration from Environment Variables
const COGNITO_CONFIG = {
  userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || '',
  userPoolClientId: process.env.REACT_APP_COGNITO_CLIENT_ID || '',
  cognitoDomain: process.env.REACT_APP_COGNITO_DOMAIN || '',
  redirectSignIn: process.env.REACT_APP_COGNITO_REDIRECT_SIGN_IN || 'http://localhost:3000/callback',
  redirectSignOut: process.env.REACT_APP_COGNITO_REDIRECT_SIGN_OUT || 'http://localhost:3000'
};

// Configure Amplify with your Cognito User Pool
const amplifyConfig = {
  Auth: {
    Cognito: {
      userPoolId: COGNITO_CONFIG.userPoolId,
      userPoolClientId: COGNITO_CONFIG.userPoolClientId,
      signUpVerificationMethod: 'code' as const,
      loginWith: {
        email: true,
        username: true,
      }
    }
  }
};

// Role conversion utilities
const convertBackendRoleToFrontend = (backendRole: string): string => {
  const roleMapping: Record<string, string> = {
    'TenantAdmin': 'Administrator',
    'TenantPowerUser': 'PowerUser',
    'TenantUser': 'ReadOnly',
    'Administrator': 'Administrator',
    'PowerUser': 'PowerUser',
    'ReadOnly': 'ReadOnly',
  };
  return roleMapping[backendRole] || 'ReadOnly';
};


// Validate required environment variables
const requiredEnvVars = [
  'REACT_APP_COGNITO_USER_POOL_ID',
  'REACT_APP_COGNITO_CLIENT_ID',
  'REACT_APP_COGNITO_DOMAIN'
];

const missingEnvVars = requiredEnvVars.filter(varName => !process.env[varName]);

if (missingEnvVars.length > 0) {
  logger.error('Missing required environment variables:', missingEnvVars.join(', '));
  logger.error('Please check your .env file and ensure all required Cognito environment variables are set.');
  logger.error('Make sure the .env file is in the /tagops/frontend/ directory and restart your development server.');
}

// Verify configuration before applying
if (!amplifyConfig.Auth.Cognito.userPoolId || !amplifyConfig.Auth.Cognito.userPoolClientId) {
  logger.error('Invalid Amplify configuration - missing required fields');
} else {
  Amplify.configure(amplifyConfig);
}

interface User {
  username: string;
  email: string;
  tenantId?: string;
  role?: string;
  attributes: {
    email: string;
    email_verified: boolean;
    sub: string;
    tenant_id?: string;
    userRole?: string;
    given_name?: string;
    family_name?: string;
  };
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signIn: (username: string, password: string) => Promise<void>;
  signUp: (username: string, email: string, password: string, tenantId: string) => Promise<void>;
  signOut: () => Promise<void>;
  confirmSignUp: (username: string, code: string) => Promise<void>;
  forgotPassword: (username: string) => Promise<void>;
  confirmForgotPassword: (username: string, code: string, newPassword: string) => Promise<void>;
  getCurrentUser: () => Promise<User | null>;
  redirectToCognitoLogin: () => void;
  redirectToCognitoSignOut: () => void;
  handleTokenExpiration: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showLogoutNotification, setShowLogoutNotification] = useState(false);
  const [showSessionWarning, setShowSessionWarning] = useState(false);

  // Check for existing user on mount
  useEffect(() => {
    const checkAuthState = async () => {
      try {
        // Check if we have stored tokens and user info
        const accessToken = localStorage.getItem('access_token');
        const idToken = localStorage.getItem('id_token');
        const tokenExpiresAt = localStorage.getItem('token_expires_at');
        const userInfoStr = localStorage.getItem('user_info');
        
        if (accessToken && idToken && tokenExpiresAt && userInfoStr) {
          const expiresAt = parseInt(tokenExpiresAt);
          const now = Date.now();
          
          // Check if token is still valid (with 2 minute buffer)
          if (now < expiresAt - 2 * 60 * 1000) {
            const userInfo = JSON.parse(userInfoStr);
            
            // Extract role from token
            const userRole = userInfo['custom:userRole'] || userInfo.userRole;
            const frontendRole = userRole ? convertBackendRoleToFrontend(userRole) : 'ReadOnly';
            
            const realUser: User = {
              username: userInfo.username || userInfo.sub,
              email: userInfo.email || '',
              tenantId: userInfo['custom:tenant_id'],
              role: frontendRole,
              attributes: {
                email: userInfo.email || '',
                email_verified: userInfo.email_verified === 'true',
                sub: userInfo.sub || '',
                tenant_id: userInfo['custom:tenant_id'],
                userRole: userRole,
                given_name: userInfo.given_name,
                family_name: userInfo.family_name
              }
            };
            
            setUser(realUser);
            setIsAuthenticated(true);
            
            // Reset session manager for new authentication
            sessionManager.resetSession();
                    } else {
            // Token expired, try to refresh first
            try {
              await tokenManager.refreshTokens();
              // If refresh succeeds, continue with normal flow
              const refreshedUserInfo = JSON.parse(localStorage.getItem('user_info') || '{}');
              const userRole = refreshedUserInfo['custom:userRole'] || refreshedUserInfo.userRole;
              const frontendRole = userRole ? convertBackendRoleToFrontend(userRole) : 'ReadOnly';
              
              const realUser: User = {
                username: refreshedUserInfo.username || refreshedUserInfo.sub,
                email: refreshedUserInfo.email || '',
                tenantId: refreshedUserInfo['custom:tenant_id'],
                role: frontendRole,
                attributes: {
                  email: refreshedUserInfo.email || '',
                  email_verified: refreshedUserInfo.email_verified === 'true',
                  sub: refreshedUserInfo.sub || '',
                  tenant_id: refreshedUserInfo['custom:tenant_id'],
                  userRole: userRole,
                  given_name: refreshedUserInfo.given_name,
                  family_name: refreshedUserInfo.family_name
                }
              };
              
              setUser(realUser);
              setIsAuthenticated(true);
              sessionManager.resetSession();
            } catch (refreshError) {
              // Refresh failed, clear everything and redirect to login
              tokenManager.clearTokens();
              
              setUser(null);
              setIsAuthenticated(false);
              
              // Show notification before redirecting
              setShowLogoutNotification(true);
              
              // Redirect to Cognito login after a short delay
              setTimeout(() => {
                redirectToCognitoLogin();
              }, 3000); // 3 second delay to show notification
            }
          }
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthState();
  }, []);

  // Set up session management
  useEffect(() => {
    if (isAuthenticated) {
      console.log('Setting up session management for authenticated user');
      
      // Set up session warning callback first
      sessionManager.setWarningCallback(() => {
        console.log('Session warning callback triggered');
        setShowSessionWarning(true);
      });

      // Set up session logout callback
      sessionManager.setLogoutCallback(() => {
        console.log('Session logout callback triggered');
        // Clear all stored tokens and user info
        tokenManager.clearTokens();
        
        // Clear all localStorage, sessionStorage, cookies, and cache
        clearAllStorage();
        
        setShowLogoutNotification(true);
        setUser(null);
        setIsAuthenticated(false);
        setTimeout(() => {
          redirectToCognitoLogin();
        }, 3000);
      });
      
      // Reset session manager for new authentication (this will start monitoring)
      sessionManager.resetSession();

      // Add testing methods to window object for debugging
      (window as any).testSession = {
        checkSession: () => sessionManager.manualSessionCheck(),
        simulateActivity: () => sessionManager.simulateActivity(),
        getSessionInfo: () => sessionManager.getSessionInfo(),
        getStatus: () => sessionManager.getStatus(),
        forceLogout: () => sessionManager['forceLogout']('Manual test logout'),
        enableTestMode: () => sessionManager.enableTestMode(),
        enableProductionMode: () => sessionManager.enableProductionMode(),
        forceStartMonitoring: () => sessionManager.forceStartMonitoring(),
        checkTokens: () => {
          console.log('Token Info:', tokenManager.getTokenInfo());
          console.log('Tokens expired:', tokenManager.areTokensExpired());
          console.log('Should refresh:', tokenManager.shouldRefreshTokens());
        }
      };
      
      // Log session manager status after setup
      console.log('Session manager status after setup:', sessionManager.getStatus());
    } else {
      console.log('User not authenticated, destroying session manager');
      // Clean up session manager when user is not authenticated
      sessionManager.destroy();
      
      // Remove testing methods from window object
      delete (window as any).testSession;
    }

    // Don't destroy session manager in cleanup - let it run for the full timeout
    return () => {
      // Only remove testing methods, don't destroy session manager
      delete (window as any).testSession;
    };
  }, [isAuthenticated]);

  // Listen for logout notification events from axios interceptor
  useEffect(() => {
    const handleLogoutNotification = () => {
      setShowLogoutNotification(true);
    };

    window.addEventListener('showLogoutNotification', handleLogoutNotification);

    return () => {
      window.removeEventListener('showLogoutNotification', handleLogoutNotification);
    };
  }, []);

  // Clean up session manager only when component unmounts completely
  useEffect(() => {
    return () => {
      // Only destroy session manager when the entire component unmounts
      console.log('AuthContext unmounting, destroying session manager');
      sessionManager.destroy();
    };
  }, []);


  const getCurrentUser = async (): Promise<User | null> => {
    return user;
  };

  const handleSignIn = async (username: string, password: string): Promise<void> => {
    try {
      await signIn({ username, password });
      const currentUser = await getCurrentUser();
      if (!currentUser) {
        throw new Error('Failed to get current user after sign in');
      }
      
      const attributes = await fetchUserAttributes();
      
      // Extract role from attributes
      const userRole = attributes['custom:userRole'] || attributes.userRole;
      const frontendRole = userRole ? convertBackendRoleToFrontend(userRole) : 'ReadOnly';
      
      const userData: User = {
        username: currentUser.username,
        email: attributes.email || '',
        tenantId: attributes['custom:tenant_id'] || attributes.tenant_id,
        role: frontendRole,
        attributes: {
          email: attributes.email || '',
          email_verified: attributes.email_verified === 'true',
          sub: attributes.sub || '',
          tenant_id: attributes['custom:tenant_id'] || attributes.tenant_id,
          userRole: userRole,
          given_name: attributes.given_name,
          family_name: attributes.family_name
        }
      };
      
      setUser(userData);
      setIsAuthenticated(true);
      
      // Reset session manager for new authentication
      sessionManager.resetSession();
    } catch (error) {
      logger.error('Error signing in:', error);
      throw error;
    }
  };

  const handleSignUp = async (username: string, email: string, password: string, tenantId: string): Promise<void> => {
    try {
      await signUp({
        username,
        password,
        options: {
          userAttributes: {
            email,
            'custom:tenant_id': tenantId
          }
        }
      });
    } catch (error) {
      logger.error('Error signing up:', error);
      throw error;
    }
  };

  const handleConfirmSignUp = async (username: string, code: string): Promise<void> => {
    try {
      await confirmSignUp({ username, confirmationCode: code });
    } catch (error) {
      logger.error('Error confirming sign up:', error);
      throw error;
    }
  };

  const handleSignOut = async (): Promise<void> => {
    try {
      // Sign out from Cognito
      await signOut();
      
      // Clear all stored tokens and user info
      tokenManager.clearTokens();
      
      // Clear all localStorage, sessionStorage, cookies, and cache
      clearAllStorage();
      
      // Update state immediately
      setUser(null);
      setIsAuthenticated(false);
      
      // Clear session manager
      sessionManager.destroy();
      
      // Redirect to Cognito sign out page
      redirectToCognitoSignOut();
    } catch (error) {
      logger.error('Error signing out:', error);
      // Even if Cognito sign out fails, clear local state and redirect
      tokenManager.clearTokens();
      
      // Clear all localStorage, sessionStorage, cookies, and cache
      clearAllStorage();
      
      setUser(null);
      setIsAuthenticated(false);
      sessionManager.destroy();
      
      redirectToCognitoSignOut();
    }
  };

  // Function to handle token expiration
  const handleTokenExpiration = () => {
    // Clear all stored tokens and user info
    tokenManager.clearTokens();
    
    // Clear all localStorage, sessionStorage, cookies, and cache
    clearAllStorage();
    
    // Update state immediately
    setUser(null);
    setIsAuthenticated(false);
    
    // Clear session manager
    sessionManager.destroy();
    
    // Show notification before redirecting
    setShowLogoutNotification(true);
    
    // Redirect to Cognito login after a short delay
    setTimeout(() => {
      redirectToCognitoLogin();
    }, 3000); // 3 second delay to show notification
  };

  const handleForgotPassword = async (username: string): Promise<void> => {
    try {
      await resetPassword({ username });
    } catch (error) {
      logger.error('Error requesting password reset:', error);
      throw error;
    }
  };

  const handleConfirmForgotPassword = async (username: string, code: string, newPassword: string): Promise<void> => {
    try {
      await confirmResetPassword({ username, confirmationCode: code, newPassword });
    } catch (error) {
      logger.error('Error confirming password reset:', error);
      throw error;
    }
  };

  const redirectToCognitoLogin = () => {
    const cognitoDomain = COGNITO_CONFIG.cognitoDomain;
    const clientId = COGNITO_CONFIG.userPoolClientId;
    const redirectUri = COGNITO_CONFIG.redirectSignIn;
    
    const loginUrl = `https://${cognitoDomain}/login?client_id=${clientId}&response_type=code&scope=email+openid+profile&redirect_uri=${encodeURIComponent(redirectUri)}`;
    
    window.location.href = loginUrl;
  };

  const redirectToCognitoSignOut = () => {
    const cognitoDomain = COGNITO_CONFIG.cognitoDomain;
    const clientId = COGNITO_CONFIG.userPoolClientId;
    const redirectUri = COGNITO_CONFIG.redirectSignOut;
    
    const logoutUrl = `https://${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(redirectUri)}`;
    
    window.location.href = logoutUrl;
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    signIn: handleSignIn,
    signUp: handleSignUp,
    signOut: handleSignOut,
    confirmSignUp: handleConfirmSignUp,
    forgotPassword: handleForgotPassword,
    confirmForgotPassword: handleConfirmForgotPassword,
    getCurrentUser,
    redirectToCognitoLogin,
    redirectToCognitoSignOut,
    handleTokenExpiration
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      
      {/* Logout Notification */}
      <Snackbar
        open={showLogoutNotification}
        autoHideDuration={3000}
        onClose={() => setShowLogoutNotification(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowLogoutNotification(false)} 
          severity="warning" 
          sx={{ width: '100%' }}
        >
          Your session has expired. Please log in again.
        </Alert>
      </Snackbar>

      {/* Session Warning Notification */}
      <Snackbar
        open={showSessionWarning}
        autoHideDuration={60000}
        onClose={() => setShowSessionWarning(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowSessionWarning(false)} 
          severity="info" 
          sx={{ width: '100%' }}
        >
          Your session will expire soon due to inactivity. Please interact with the page to extend your session.
        </Alert>
      </Snackbar>
    </AuthContext.Provider>
  );
};
